import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from "axios"
import { API_BASE_URL, API_TIMEOUT, ENABLE_LOGGING } from "../config/environment"
import { getDefaultHeaders, getUploadHeaders, logHeaders } from "../lib/request-utils"

// 请求配置接口
export interface RequestConfig extends Partial<InternalAxiosRequestConfig> {
  skipAuth?: boolean // 跳过认证
  retry?: boolean // 是否重试
  retryCount?: number // 重试次数
}

// 服务端响应接口
export interface ServerResponse<T = any> {
  success: boolean
  errorLevel: string
  message: string
  code: number | null
  data: T
}

// 错误信息接口
export interface ApiError {
  code: number
  message: string
  details?: any
}

// 创建axios实例
const createHttpInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
      "Content-Type": "application/json",
      "biz": "index",
    },
  })

  // 请求拦截器
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // 根据URL设置X-Biz-Id
      const url = config.url || ""
      let bizId = 'index'
      if (url.startsWith('/uc/')) {
        bizId = 'usercenter'
      } else if (url.startsWith('/agent/')) {
        bizId = 'agentcreator'
      }
      config.headers['X-Biz-Id'] = bizId
      
      // 添加认证token
      const token = localStorage.getItem("token")
      if (token && !(config as RequestConfig).skipAuth) {
        config.headers.Authorization = `Bearer ${token}`
      }

      // 开发环境日志
      if (ENABLE_LOGGING) {
        logHeaders(config.headers as Record<string, string>, config.url || "")
        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
          data: config.data,
          params: config.params,
        })
      }

      return config
    },
    (error: AxiosError) => {
      if (ENABLE_LOGGING) {
        console.error("❌ Request Error:", error)
      }
      return Promise.reject(error)
    }
  )

  // 响应拦截器
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      // 开发环境日志
      if (ENABLE_LOGGING) {
        console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
          status: response.status,
          data: response.data,
        })
      }

      // 检查网关返回的错误
      if (response.data && response.data.success === false) {
        const gatewayError: ApiError = {
          code: response.data.code || response.status,
          message: response.data.message || "Gateway error",
          details: {
            errorLevel: response.data.errorLevel,
            data: response.data.data
          }
        }
        return Promise.reject(gatewayError)
      }

      // 检查嵌套的错误结构：外层success: true，内层data.success: false
      if (response.data && response.data.success === true && response.data.data && typeof response.data.data === 'object') {
        const innerData = response.data.data
        if (innerData.success === false) {
          const nestedError: ApiError = {
            code: innerData.code || response.status,
            message: innerData.message || "Nested error",
            details: {
              errorLevel: response.data.errorLevel,
              data: innerData
            }
          }
          return Promise.reject(nestedError)
        }
      }

      return response
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as RequestConfig

      // 开发环境日志
      if (ENABLE_LOGGING) {
        console.error("❌ Response Error:", {
          status: error.response?.status,
          message: error.response?.data,
          url: error.config?.url,
        })
      }

      // 处理401未授权错误
      if (error.response?.status === 401 && !originalRequest?.skipAuth) {
        // 清除本地token
        localStorage.removeItem("token")
        localStorage.removeItem("user")
        
        // 重定向到登录页
        if (typeof window !== "undefined") {
          window.location.href = "/login"
        }
      }

      // 处理网络错误
      if (!error.response) {
        const networkError: ApiError = {
          code: 0,
          message: "Network error. Please check your connection.",
        }
        return Promise.reject(networkError)
      }

      // 处理服务器错误
      const serverError: ApiError = {
        code: error.response.status,
        message: (error.response.data as any)?.message || "Server error occurred",
        details: error.response.data,
      }

      return Promise.reject(serverError)
    }
  )

  return instance
}

// 创建HTTP实例
export const http = createHttpInstance()

// HTTP请求方法封装
export class HttpService {
  // GET请求
  static async get<T = any>(url: string, config?: RequestConfig): Promise<T> {
    try {
      let bizId = 'index'
      if (url.startsWith('/uc/')) {
        bizId = 'usercenter'
      } else if (url.startsWith('/agent/')) {
        bizId = 'agentcreator'
      }
      const response = await http.get<ServerResponse<T>>(url, {
        ...config,
        headers: {
          ...getDefaultHeaders(bizId),
          ...config?.headers,
        }
      })
      return (response.data as ServerResponse<T>).data
    } catch (error) {
      throw error
    }
  }

  // POST请求
  static async post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    try {
      let bizId = 'index'
      if (url.startsWith('/uc/')) {
        bizId = 'usercenter'
      } else if (url.startsWith('/agent/')) {
        bizId = 'agentcreator'
      }
      const response = await http.post<ServerResponse<T>>(url, data, {
        ...config,
        headers: {
          ...getDefaultHeaders(bizId),
          ...config?.headers,
        }
      })
      return (response.data as ServerResponse<T>).data
    } catch (error) {
      throw error
    }
  }

  // PUT请求
  static async put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    try {
      let bizId = 'index'
      if (url.startsWith('/uc/')) {
        bizId = 'usercenter'
      } else if (url.startsWith('/agent/')) {
        bizId = 'agentcreator'
      }
      const response = await http.put<ServerResponse<T>>(url, data, {
        ...config,
        headers: {
          ...getDefaultHeaders(bizId),
          ...config?.headers,
        }
      })
      return (response.data as ServerResponse<T>).data
    } catch (error) {
      throw error
    }
  }

  // DELETE请求
  static async delete<T = any>(url: string, config?: RequestConfig): Promise<T> {
    try {
      let bizId = 'index'
      if (url.startsWith('/uc/')) {
        bizId = 'usercenter'
      } else if (url.startsWith('/agent/')) {
        bizId = 'agentcreator'
      }
      const response = await http.delete<ServerResponse<T>>(url, {
        ...config,
        headers: {
          ...getDefaultHeaders(bizId),
          ...config?.headers,
        }
      })
      return (response.data as ServerResponse<T>).data
    } catch (error) {
      throw error
    }
  }

  // PATCH请求
  static async patch<T = any>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    try {
      let bizId = 'index'
      if (url.startsWith('/uc/')) {
        bizId = 'usercenter'
      } else if (url.startsWith('/agent/')) {
        bizId = 'agentcreator'
      }
      const response = await http.patch<ServerResponse<T>>(url, data, {
        ...config,
        headers: {
          ...getDefaultHeaders(bizId),
          ...config?.headers,
        }
      })
      return (response.data as ServerResponse<T>).data
    } catch (error) {
      throw error
    }
  }

  // 文件上传
  static async upload<T = any>(url: string, file: File, config?: RequestConfig): Promise<T> {
    try {
      const formData = new FormData()
      formData.append("file", file)
      
      let bizId = 'index'
      if (url.startsWith('/uc/')) {
        bizId = 'usercenter'
      } else if (url.startsWith('/agent/')) {
        bizId = 'agentcreator'
      }
      const response = await http.post<ServerResponse<T>>(url, formData, {
        ...config,
        headers: {
          ...getUploadHeaders(bizId),
          ...config?.headers,
        },
      })
      return (response.data as ServerResponse<T>).data
    } catch (error) {
      throw error
    }
  }
}

export default http 